import { dashboardReference } from '@/lib/dashboardReferences';
import {
    type DashboardSummary,
    SHARED_DASHBOARDS_KEY,
    deleteDashboard,
    exportDashboardAsApp,
    getDashboard,
    listDashboards,
    renameDashboard,
    setDashboardListing,
    shareDashboard
} from '@/lib/dashboards-api';
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { toast } from 'sonner';
import useSWR, { mutate as refreshCache } from 'swr';

import {
    threadHistoryState,
    useChatInteract,
    useConfig
} from '@chainlit/react-client';

import {
    ArtifactCards,
    ArtifactCardsSkeleton
} from '@/components/Artifacts/ArtifactCards';
import { ArtifactDashboardView } from '@/components/Artifacts/ArtifactDashboardView';
import { ArtifactDetailPanel } from '@/components/Artifacts/ArtifactDetailPanel';
import { DashboardAccessDialog } from '@/components/Artifacts/DashboardAccessDialog';
import { DASHBOARD_LIST_KEY } from '@/components/Dashboards/useDashboardTitles';
import { MobileNavTrigger } from '@/components/ScheduledAgents/MobileNavTrigger';
import { Input } from '@/components/ui/input';

import { dashboardReferencesState } from '@/state/chat';

export default function Artifacts() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { config } = useConfig();
    const { clear } = useChatInteract();
    const setThreadHistory = useSetRecoilState(threadHistoryState);
    const setDashboardReferences = useSetRecoilState(dashboardReferencesState);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [accessFor, setAccessFor] = useState<DashboardSummary | null>(null);

    const artifactsEnabled = config?.features?.artifacts_enabled === true;
    const organizationArtifactsEnabled =
        config?.features?.organization_artifacts_enabled === true;

    useEffect(() => {
        setThreadHistory((prev) =>
            prev?.currentThreadId === '__none__'
                ? prev
                : { ...prev, currentThreadId: '__none__' }
        );
    }, [setThreadHistory]);

    const {
        data: dashboards = [],
        error,
        isLoading
    } = useSWR(artifactsEnabled ? DASHBOARD_LIST_KEY : null, listDashboards, {
        revalidateOnFocus: false
    });

    if (config && !artifactsEnabled) {
        return <Navigate to="/" replace />;
    }

    if (id) {
        return <ArtifactDashboardView key={id} dashboardId={id} />;
    }

    const startChat = async (dashboard: DashboardSummary) => {
        try {
            const full = await getDashboard(dashboard.id);
            window.dispatchEvent(new CustomEvent('newChat'));
            clear();
            navigate('/');
            setDashboardReferences([dashboardReference(full)]);
        } catch (startError) {
            toast.error(
                startError instanceof Error
                    ? `Could not start a chat: ${startError.message}`
                    : 'Could not start a chat'
            );
        }
    };

    const query = searchQuery.trim().toLowerCase();
    const visibleDashboards = query
        ? dashboards.filter((dashboard) =>
              dashboard.title.toLowerCase().includes(query)
          )
        : dashboards;
    const selected = dashboards.find(
        (dashboard) => dashboard.id === selectedId
    );

    return (
        <div className="flex size-full">
            <div className="relative flex flex-col flex-1 bg-background min-w-0">
                <MobileNavTrigger />
                <div className="mx-auto px-3 sm:px-12 pt-8 sm:pt-12 pb-6 sm:pb-8 w-full max-w-agentPageWidth">
                    <div className="flex flex-col gap-4 sm:gap-6">
                        <div className="flex flex-col gap-1">
                            <h1 className="font-medium text-foreground text-2xl lg:text-3xl">
                                Artifacts
                            </h1>
                            <p className="text-muted-foreground text-sm sm:text-lg">
                                Your dashboards and the other things the agent
                                has made for you
                            </p>
                        </div>
                        <div className="relative">
                            <Search className="top-1/2 left-3 absolute size-4 sm:size-5 text-muted-foreground -translate-y-1/2" />
                            <Input
                                type="text"
                                placeholder="Search artifacts..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 sm:pl-10 h-9 sm:h-10"
                            />
                        </div>
                    </div>
                </div>

                <div className="flex-1 mx-auto px-3 sm:px-12 pt-1 pb-6 sm:pb-8 w-full max-w-agentPageWidth overflow-auto [scrollbar-gutter:stable]">
                    {isLoading ? (
                        <ArtifactCardsSkeleton />
                    ) : error ? (
                        <div className="flex justify-center items-center py-8 text-destructive text-sm">
                            Artifacts could not be loaded
                        </div>
                    ) : visibleDashboards.length === 0 ? (
                        <div className="flex justify-center items-center py-8 text-muted-foreground text-sm">
                            {query
                                ? 'No artifacts match your search'
                                : 'No artifacts yet'}
                        </div>
                    ) : (
                        <ArtifactCards
                            dashboards={visibleDashboards}
                            selectedId={selectedId}
                            onSelect={setSelectedId}
                            canManage={() => organizationArtifactsEnabled}
                            onManage={setAccessFor}
                        />
                    )}
                </div>

                {accessFor && (
                    <DashboardAccessDialog
                        dashboardId={accessFor.id}
                        title={accessFor.title}
                        open
                        showOrganizationNotice
                        onOpenChange={(open) => !open && setAccessFor(null)}
                    />
                )}
            </div>

            {selected && (
                <ArtifactDetailPanel
                    dashboard={selected}
                    onClose={() => setSelectedId(null)}
                    onOpen={() => navigate(`/artifacts/${selected.id}`)}
                    onStartChat={() => startChat(selected)}
                    onRename={async (title) => {
                        await renameDashboard(
                            await getDashboard(selected.id),
                            title
                        );
                        await refreshCache(['dashboard', selected.id]);
                        await refreshCache(DASHBOARD_LIST_KEY);
                        await refreshCache(SHARED_DASHBOARDS_KEY);
                    }}
                    onToggleShare={
                        organizationArtifactsEnabled
                            ? async (shared) => {
                                  await shareDashboard(selected.id, shared);
                                  await refreshCache(DASHBOARD_LIST_KEY);
                                  await refreshCache(SHARED_DASHBOARDS_KEY);
                              }
                            : undefined
                    }
                    onExportToApps={async (name, grouping, access) => {
                        await exportDashboardAsApp(
                            selected.id,
                            name,
                            grouping,
                            access
                        );
                        await refreshCache(['dashboard', selected.id]);
                        await refreshCache(DASHBOARD_LIST_KEY);
                        await refreshCache(SHARED_DASHBOARDS_KEY);
                    }}
                    onToggleListing={async (visible) => {
                        await setDashboardListing(selected.id, visible);
                        await refreshCache(DASHBOARD_LIST_KEY);
                        await refreshCache(SHARED_DASHBOARDS_KEY);
                    }}
                    onDelete={async () => {
                        await deleteDashboard(selected.id);
                        await refreshCache(DASHBOARD_LIST_KEY);
                        setSelectedId(null);
                    }}
                />
            )}
        </div>
    );
}
